import * as React from 'react';
import React__default, { useMemo, useState, useEffect, useCallback } from 'react';
import { Selectors, State } from '@grandvisionhq/auth';
import { Button, toast } from '@grandvisionhq/common';
import { useFeatureToggles, useSelector, useIntl } from '@grandvisionhq/state';
import { gql, useLazyQuery, useMutation } from '@grandvisionhq/graphql';
import classnames from 'classnames';
import { CheckboxWithLabel } from '@grandvisionhq/forms';
import { DataLayer } from '@grandvisionhq/analytics';

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}

function extends_() {
    extends_ = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return extends_.apply(this, arguments);
}
function _extends() {
    return extends_.apply(this, arguments);
}

var UIType;
(function(UIType) {
    UIType["CHECKBOX"] = "CHECKBOX";
})(UIType || (UIType = {}));

const CREATE_EYE_EXAM = gql`
  mutation CreateEyeExam($eyeExamInput: CreateEyeExamInput!) {
    createEyeExam(input: $eyeExamInput) {
      code
      success
      message
      eyeExam {
        examId
        examUrl
      }
    }
  }
`;

const SAVE_NEW_CONSENT = gql`
  mutation SaveNewConsent($customerId: ID!, $consents: [ConsentInput!]!) {
    saveNewConsents(customerId: $customerId, consents: $consents) {
      customerId
      consentName
      success
      errorMessage
    }
  }
`;

const CHECK_CUSTOMER_CONSENTS = gql`
  query CheckCustomerConsents($customerId: ID!, $consentName: String!) {
    checkCustomerConsents(customerId: $customerId, consentName: $consentName)
  }
`;

function isRecoverableError(error) {
    return error.type !== 'CHECK_CONSENT';
}
function createEyeExamError(type, originalError) {
    return {
        type,
        originalError: originalError instanceof Error ? originalError : new Error('UNKNOWN_ERROR')
    };
}

/*
 * Check the eye-exam consent, create a new eye exam and returns the url. If the consent is not yet given, return the value of the consent and a function to save the consent.
 */ const useCreateEyeExam = ()=>{
    var ref3, ref1, ref2;
    const { featureConfigs  } = useFeatureToggles();
    // Check auth state
    const auth = useSelector(Selectors.getAuth);
    const isLoggedIn = State.isLoggedIn(auth);
    const isAuthLoading = State.getLoggedInState(auth) === 'loading';
    // @TODO add the correct error code mapped to BR label after https://jira.grandvision.global/browse/MACC-9133 is finished
    const [error, setError] = useState(null);
    const [checkConsent, checkConsentResult] = useLazyQuery(CHECK_CUSTOMER_CONSENTS);
    const [saveConsentMutation, saveResult] = useMutation(SAVE_NEW_CONSENT);
    const [createEyeExamUrl, createEyeExamMutation] = useMutation(CREATE_EYE_EXAM);
    // Consent name default is eye-exam, but can be changed by the opco
    const consentName = featureConfigs.eyeExamConsentKey;
    const loading = isAuthLoading || !checkConsentResult.called || checkConsentResult.loading || saveResult.loading || createEyeExamMutation.loading;
    const hasConsentOnLoad = checkConsentResult === null || checkConsentResult === void 0 ? void 0 : (ref3 = checkConsentResult.data) === null || ref3 === void 0 ? void 0 : ref3.checkCustomerConsents;
    const eyeExamUrl = (ref2 = (ref1 = createEyeExamMutation.data) === null || ref1 === void 0 ? void 0 : ref1.createEyeExam.eyeExam) === null || ref2 === void 0 ? void 0 : ref2.examUrl;
    // Check the eye-exam consent value if the user is logged in
    useEffect(()=>{
        if (isLoggedIn && !hasConsentOnLoad) {
            checkConsent({
                variables: {
                    consentName,
                    customerId: auth.userId
                }
            });
        }
    }, [
        isLoggedIn,
        checkConsent,
        hasConsentOnLoad,
        auth === null || auth === void 0 ? void 0 : auth.userId,
        consentName
    ]);
    const saveConsent = useCallback((consentText)=>{
        return saveConsentMutation({
            variables: {
                customerId: auth.userId,
                consents: [
                    {
                        consentName,
                        uiType: UIType.CHECKBOX,
                        value: true,
                        literalText: consentText
                    }, 
                ]
            }
        });
    }, [
        auth.userId,
        consentName,
        saveConsentMutation
    ]);
    const createEyeExam = useCallback(function() {
        var _ref = _asyncToGenerator(function*(consentText) {
            setError(null);
            if (!hasConsentOnLoad) {
                try {
                    var ref;
                    const result = yield saveConsent(consentText);
                    if (!(result === null || result === void 0 ? void 0 : (ref = result.data) === null || ref === void 0 ? void 0 : ref.saveNewConsents)) return;
                } catch (err) {
                    setError(createEyeExamError('SAVE_CONSENT', err));
                    return;
                }
            }
            try {
                return yield createEyeExamUrl({
                    variables: {
                        eyeExamInput: {
                            userId: auth.userId,
                            language: 'auto'
                        }
                    }
                });
            } catch (err) {
                setError(createEyeExamError('CREATE_URL', err));
            }
        });
        return function(consentText) {
            return _ref.apply(this, arguments);
        };
    }(), [
        saveConsent,
        createEyeExamUrl,
        auth.userId,
        hasConsentOnLoad
    ]);
    useEffect(()=>{
        // since checkConsent won't throw an error, we have to check it here
        if (checkConsentResult.error) {
            setError(createEyeExamError('CHECK_CONSENT', checkConsentResult.error));
        }
    }, [
        checkConsentResult.error
    ]);
    return useMemo(()=>{
        return {
            createEyeExam,
            eyeExamUrl,
            hasConsentOnLoad,
            loading,
            error
        };
    }, [
        hasConsentOnLoad,
        loading,
        error,
        createEyeExam,
        eyeExamUrl
    ]);
};

const LaunchEyeExam = ({ className , consentValue , hideConsent , errorMissingConsent , onChangeConsent , onSubmit , isLoading , labels ,  })=>{
    return /*#__PURE__*/ React.createElement("form", {
        noValidate: true,
        className: classnames('launch-eye-exam', className),
        onSubmit: onSubmit
    }, !hideConsent && /*#__PURE__*/ React.createElement(CheckboxWithLabel, {
        className: "launch-eye-exam__checkbox",
        label: labels.consentText,
        id: "lauch_eye_exam",
        value: consentValue,
        error: errorMissingConsent,
        errorMessage: labels.missingConsentError,
        checked: consentValue,
        onChange: onChangeConsent,
        disabled: isLoading
    }), /*#__PURE__*/ React.createElement(Button, {
        type: "submit",
        caption: labels.caption,
        isLoading: isLoading
    }));
};

const LoginForEyeExam = (props)=>/*#__PURE__*/ React.createElement(Button, _extends({}, props))
;

const LaunchEyeExamContainer = ({ onLogin , onStartEyeExam , onConsent  })=>{
    const { getLabel  } = useIntl();
    const auth = useSelector(Selectors.getAuth);
    const formattedRoutes = useSelector(Selectors.getFormattedRoutes);
    const isAuthLoading = State.getLoggedInState(auth) === 'loading';
    const loginRoute = (formattedRoutes === null || formattedRoutes === void 0 ? void 0 : formattedRoutes.login) || '';
    const { createEyeExam , eyeExamUrl , hasConsentOnLoad , loading , error  } = useCreateEyeExam();
    const [consentIsChecked, setConsentIsChecked] = React__default.useState(!!hasConsentOnLoad);
    const [errorMissingConsent, setErrorMissingConsent] = React__default.useState();
    const isLoading = isAuthLoading || loading;
    const hideConsent = isLoading || hasConsentOnLoad === true;
    const labels = React__default.useMemo(()=>({
            caption: getLabel('elements.eyeExamCta.redirectToExam'),
            loggedOutCaption: getLabel('elements.eyeExamCta.loginAction'),
            consentText: getLabel('elements.eyeExam.consent'),
            missingConsentError: getLabel('elements.eyeExam.consent.missing.error')
        })
    , [
        getLabel
    ]);
    const handleSubmit = React__default.useCallback(function() {
        var _ref = _asyncToGenerator(function*(event) {
            var ref, ref1, ref2;
            event.preventDefault();
            setErrorMissingConsent(false);
            if (!hasConsentOnLoad && !consentIsChecked) {
                setErrorMissingConsent(true);
                return;
            }
            onStartEyeExam === null || onStartEyeExam === void 0 ? void 0 : onStartEyeExam();
            if (eyeExamUrl) {
                return window.location.assign(eyeExamUrl);
            }
            const result = yield createEyeExam(labels.consentText);
            const url = result === null || result === void 0 ? void 0 : (ref = result.data) === null || ref === void 0 ? void 0 : (ref1 = ref.createEyeExam) === null || ref1 === void 0 ? void 0 : (ref2 = ref1.eyeExam) === null || ref2 === void 0 ? void 0 : ref2.examUrl;
            if (url) {
                window.location.assign(url);
            } else {
                toast.failed('common.errors.recoverable.generic');
            }
        });
        return function(event) {
            return _ref.apply(this, arguments);
        };
    }(), [
        hasConsentOnLoad,
        consentIsChecked,
        createEyeExam,
        labels,
        eyeExamUrl,
        onStartEyeExam
    ]);
    const handleChangeConsent = React__default.useCallback(()=>{
        onConsent === null || onConsent === void 0 ? void 0 : onConsent(!consentIsChecked);
        setConsentIsChecked(!consentIsChecked);
    }, [
        setConsentIsChecked,
        consentIsChecked,
        onConsent
    ]);
    React__default.useEffect(()=>{
        // checkConsent is nonrecoverable but we don't want to show the error
        // instead we will show the consent checkbox if checkConsent fails
        if (error && isRecoverableError(error)) {
            toast.failed('common.errors.recoverable.generic');
        }
    }, [
        error
    ]);
    if (State.isLoggedIn(auth)) {
        return /*#__PURE__*/ React__default.createElement(LaunchEyeExam, {
            onChangeConsent: handleChangeConsent,
            onSubmit: handleSubmit,
            isLoading: isLoading,
            consentValue: consentIsChecked,
            hideConsent: hideConsent,
            errorMissingConsent: errorMissingConsent,
            labels: labels
        });
    }
    return /*#__PURE__*/ React__default.createElement(LoginForEyeExam, {
        isLoading: isAuthLoading,
        caption: labels.loggedOutCaption,
        onClick: onLogin,
        href: typeof window !== 'undefined' ? `${loginRoute}?returnPath=${window.location.pathname}` : loginRoute
    });
};

function pushLoginAction(page) {
    DataLayer.pushEvent(createEvent('not logged', 'login to start your eye exam', page));
}
function pushCheckedConsent(page) {
    DataLayer.pushEvent(createEvent('logged', 'consent', page));
}
function pushStartEyeTest(page) {
    DataLayer.pushEvent(createEvent('logged', 'start eye test', page));
}
function createEvent(status, interaction, page) {
    return {
        event: 'VirtualTest',
        VirtualEyetest: {
            action: {
                status,
                interaction,
                page
            }
        }
    };
}

export { LaunchEyeExamContainer as LaunchEyeExam, createEvent, pushCheckedConsent, pushLoginAction, pushStartEyeTest };
//# sourceMappingURL=index.js.map
